import { getLayerIdentifier, getOrderedCopperLayers, PcbNodeTypes, PcbViaType, } from "@buildwithflux/models";
import { vec2 } from "../../../../common";
import { getAncestorUidByType, getNodesOfType, getTopLevelLayoutOfNode, PcbNodeHelper } from "../../../../nodes";
import { getConnectedCopperLayers, getTypedBakedNodeFromMap } from "../../../../pcb";
import { createCircleOrOblongShape } from "../../../../shapes";
import { getViaSegmentCanonicalType, getViaSegmentFromType } from "../../../../smart_via";
import { getModule, toMm } from "./support/utils";
const origin = { x: 0, y: 0 };
function findNet(context, netId, via) {
    if (!netId)
        return;
    if (typeof context.nodes.net === "undefined" || !context.nodes.net[netId]) {
        throw new Error(`Cannot find mapped net for via ${via.uid} (net ID: ${netId})`);
    }
    return context.nodes.net[netId];
}
const scaleSize = (size, scale) => {
    return {
        x: toMm(size.x * (scale?.x ?? 1)),
        y: toMm(size.y * (scale?.y ?? 1)),
    };
};
// Determines if a via is colocated with another via on a different smart via parent. This isn't great, but it gets the job
// done for now - this precaution was discovered to be necessary for STEP export, where colocated vias on different smart vias
// caused the STEP exporter to crash
function isViaColocated(via, pcbLayoutNodesMap) {
    const allVias = getNodesOfType(pcbLayoutNodesMap, PcbNodeTypes.via);
    const viaSmartViaParent = getTypedBakedNodeFromMap(pcbLayoutNodesMap, via.parentUid, PcbNodeTypes.smartVia);
    // Find any other vias that are colocated with this via, but on different smart vias
    const viaOtherSmartViasColocated = allVias.filter((otherVia) => {
        if (otherVia.uid === via.uid) {
            return false;
        }
        // If current via not descended from a smart via, then don't attempt to find overlappping vias from
        // other smart vias - this might need to be relaxed in future, since non-smart stitch vias could potentially
        // perfectly overlap with other vias and cause issue
        if (!viaSmartViaParent) {
            return false;
        }
        // Exclude vias on same smart via
        if (viaSmartViaParent.uid === otherVia.parentUid) {
            return false;
        }
        // Due to rounding errors, we need to use a small epsilon to determine if two vias are colocated
        return (vec2.distance(otherVia.bakedRules.positionRelativeToDocument, via.bakedRules.positionRelativeToDocument) <
            0.000001);
    });
    // If there are no other colocated vias on different smart vias, then this via is not a duplicate
    if (viaOtherSmartViasColocated.length === 0) {
        return false;
    }
    // If there are other colocated vias, only the allow via with the lower UID - this is a functional way to prevent
    // multiple vias to be exported at the same location
    return viaOtherSmartViasColocated.some((v) => via.uid > v.uid);
}
export const mapVia = (via, context, postprocess, pcbLayoutNodesMap) => {
    const netId = getAncestorUidByType(pcbLayoutNodesMap, via.uid, PcbNodeTypes.net);
    const net = findNet(context, netId, via);
    const br = via.bakedRules;
    const scaledBakedSize = scaleSize(br.size, br.scale);
    const at = {
        x: toMm(br.positionRelativeToDocument?.x ?? 0),
        y: -toMm(br.positionRelativeToDocument?.y ?? 0),
    };
    const rootLayout = getTopLevelLayoutOfNode(pcbLayoutNodesMap, via.uid);
    if (!rootLayout) {
        return null;
    }
    const stackup = getOrderedCopperLayers(rootLayout.bakedRules.stackup ?? {});
    let canonicalViaType = PcbViaType.throughHole;
    let segmentInfo;
    if (PcbNodeHelper.isNodeUnderSmartVia(via)) {
        const smartViaParentNode = pcbLayoutNodesMap[via.parentUid];
        segmentInfo = getViaSegmentFromType(br.viaType, smartViaParentNode.bakedRules.selectedViaConfigs);
        if (segmentInfo) {
            canonicalViaType = getViaSegmentCanonicalType(segmentInfo.segment, stackup);
        }
    }
    let layers = getConnectedCopperLayers(via, stackup)
        .map((fluxLayer) => context.getKiCadLayerByLayerIdentifier(via.uid, getLayerIdentifier(fluxLayer)))
        // Just remove any connected layers that don't exist in the layer stack
        // We already report this case in DRC
        .filter((layer) => layer !== null);
    // Simplify through-hole vias layer info to "*Cu" if it's on all layers
    if (canonicalViaType === PcbViaType.throughHole && layers.length === stackup.length) {
        layers = ["*.Cu"];
    }
    // Use only start and end layers for micro/blind/buried vias
    else if (canonicalViaType !== PcbViaType.throughHole && layers.length > 2) {
        const startLayer = layers.at(0);
        const endLayer = layers.at(-1);
        if (!startLayer || !endLayer) {
            throw new Error(`Cannot determine start or end layer for via ${via.uid}`);
        }
        layers = [startLayer, endLayer];
    }
    // This prevents vias from exporting, if they are colocated at the exact board coordinates, as another via,
    // from a different smart via. This is a workaround that is found to be necessary for STEP export, which doesn't
    // seem to allow perfectly overlapping via geometry
    if (isViaColocated(via, pcbLayoutNodesMap)) {
        return null;
    }
    const segmentation = 128;
    switch (canonicalViaType) {
        default:
        case PcbViaType.throughHole: {
            postprocess(() => {
                const module = getModule(br.layer, context);
                const shape = createCircleOrOblongShape(scaledBakedSize, origin, 0, segmentation, origin);
                const pts = shape.getPoints(segmentation);
                const primitives = [{ pts }];
                const drill = {
                    width: toMm(br.holeSize.x),
                    height: toMm(br.holeSize.y),
                    offset: undefined,
                    oval: br.holeSize.x !== br.holeSize.y,
                };
                // Block for Solder Mask serialization
                {
                    // Extracts geometry creation logic, used to independently write out mask geometry per board side
                    const createMaskPts = (expansionOnSide) => {
                        const size = {
                            x: scaledBakedSize.x + toMm(expansionOnSide) * 2,
                            y: scaledBakedSize.y + toMm(expansionOnSide) * 2,
                        };
                        const shape = createCircleOrOblongShape(size, at, 0, segmentation, at);
                        return shape.getPoints().concat(
                        // Reversed to change the winding order to act as a hole
                        solderMaskHoleShape.getPoints().reverse());
                    };
                    // Solder Mask hole geometry is shared between layers. If expansion from hole undefined, expansion amount defaults to zero
                    const solderMaskHoleSize = {
                        x: drill.width - toMm(br.solderMaskExpansion?.expansionFromTheHoleEdge) * 2,
                        y: drill.height - toMm(br.solderMaskExpansion?.expansionFromTheHoleEdge) * 2,
                    };
                    const solderMaskHoleShape = createCircleOrOblongShape(solderMaskHoleSize, at, 0, segmentation, at);
                    // Top Solder Mask: The criteria for solder mask on top layer being serialized - if top expansion or hole expansion present, we
                    // write out mask geometry for the top side
                    if (br.solderMaskExpansion?.topExpansion !== undefined ||
                        br.solderMaskExpansion?.expansionFromTheHoleEdge !== undefined) {
                        module.fp_poly = module.fp_poly ?? [];
                        module.fp_poly.push({
                            pts: createMaskPts(br.solderMaskExpansion.topExpansion),
                            layer: "F.Mask",
                            width: 0,
                        });
                    }
                    // Bottom Solder Mask: The criteria for solder mask on bottom layer being serialized - if bottom expansion or hole expansion present, we
                    // write out mask geometry for the bottom side
                    if (br.solderMaskExpansion?.bottomExpansion !== undefined ||
                        br.solderMaskExpansion?.expansionFromTheHoleEdge !== undefined) {
                        module.fp_poly = module.fp_poly ?? [];
                        module.fp_poly.push({
                            pts: createMaskPts(br.solderMaskExpansion.bottomExpansion),
                            layer: "B.Mask",
                            width: 0,
                        });
                    }
                }
                module.pad = module.pad ?? [];
                module.pad.push({
                    at,
                    layers,
                    locked: false,
                    size: { width: 0, height: 0 },
                    pad_id: via.uid,
                    pad_type: "thru_hole",
                    pad_shape: "custom",
                    primitives,
                    drill,
                    net,
                    remove_unused_layers: false,
                    keep_end_layers: false,
                });
            });
            return null;
        }
        case PcbViaType.blind: {
            postprocess(() => {
                const module = getModule(br.layer, context);
                const holeSize = {
                    x: toMm(br.holeSize.x),
                    y: toMm(br.holeSize.y),
                };
                const drill = {
                    width: holeSize.x,
                    height: holeSize.y,
                    offset: undefined,
                    oval: br.holeSize.x !== br.holeSize.y,
                };
                // Block for Solder Mask serialization
                {
                    // Extracts geometry creation logic, used to independently write out mask geometry per board side
                    const createMaskPts = (expansionOnSide) => {
                        const size = {
                            x: scaledBakedSize.x + toMm(expansionOnSide) * 2,
                            y: scaledBakedSize.y + toMm(expansionOnSide) * 2,
                        };
                        const shape = createCircleOrOblongShape(size, at, 0, segmentation, at);
                        return shape.getPoints().concat(
                        // Reversed to change the winding order to act as a hole
                        solderMaskHoleShape.getPoints().reverse());
                    };
                    // Solder Mask hole geometry is shared between layers. If expansion from hole undefined, expansion amount defaults to zero
                    const solderMaskHoleSize = {
                        x: drill.width - toMm(br.solderMaskExpansion?.expansionFromTheHoleEdge) * 2,
                        y: drill.height - toMm(br.solderMaskExpansion?.expansionFromTheHoleEdge) * 2,
                    };
                    const solderMaskHoleShape = createCircleOrOblongShape(solderMaskHoleSize, at, 0, segmentation, at);
                    // Top Solder Mask: The criteria for solder mask on top layer being serialized - if top expansion or hole expansion present, we
                    // write out mask geometry for the top side, AND if the segment is the first layer in the stackup
                    if (segmentInfo?.segment.startLayer === 1 &&
                        (br.solderMaskExpansion?.topExpansion !== undefined ||
                            br.solderMaskExpansion?.expansionFromTheHoleEdge !== undefined)) {
                        module.fp_poly = module.fp_poly ?? [];
                        module.fp_poly.push({
                            pts: createMaskPts(br.solderMaskExpansion.topExpansion),
                            layer: "F.Mask",
                            width: 0,
                        });
                    }
                    // Bottom Solder Mask: The criteria for solder mask on bottom layer being serialized - if bottom expansion or hole expansion present, we
                    // write out mask geometry for the bottom side, AND if the segment is the last layer in the stackup
                    if (segmentInfo?.segment.endLayer === stackup.length &&
                        (br.solderMaskExpansion?.bottomExpansion !== undefined ||
                            br.solderMaskExpansion?.expansionFromTheHoleEdge !== undefined)) {
                        module.fp_poly = module.fp_poly ?? [];
                        module.fp_poly.push({
                            pts: createMaskPts(br.solderMaskExpansion.bottomExpansion),
                            layer: "B.Mask",
                            width: 0,
                        });
                    }
                }
            });
            return {
                at,
                blind: true,
                size: scaledBakedSize.x,
                drill: toMm(br.holeSize.x),
                layers,
                net: net?.net_number,
                keep_end_layers: true,
                remove_unused_layers: true,
                zone_layer_connections: true,
            };
        }
        case PcbViaType.buried: {
            return {
                at,
                blind: true,
                size: scaledBakedSize.x,
                drill: toMm(br.holeSize.x),
                layers,
                net: net?.net_number,
                keep_end_layers: true,
                remove_unused_layers: true,
                zone_layer_connections: true,
            };
        }
        case PcbViaType.microVia: {
            return {
                at,
                micro: true,
                size: scaledBakedSize.x,
                drill: toMm(br.holeSize.x),
                layers,
                net: net?.net_number,
                keep_end_layers: true,
                remove_unused_layers: true,
                zone_layer_connections: true,
            };
        }
    }
};
//# sourceMappingURL=mapVia.js.map